/**
 * Document Picture-in-Picture helpers (Chrome / Edge).
 * Opens an always-on-top window we can portal LiveKit UI into.
 */

import { LIVE_SESSION_FLOW_CLASS } from "./useLiveSessionFlowClass";

export type DocumentPipWindow = Window & typeof globalThis;

export function isDocumentPictureInPictureSupported(): boolean {
  if (typeof window === "undefined") return false;
  const dpi = (window as unknown as { documentPictureInPicture?: unknown })
    .documentPictureInPicture as
    | { requestWindow?: unknown }
    | undefined;
  return typeof dpi?.requestWindow === "function";
}

function copyStylesToPip(pipDoc: Document): void {
  // ---------------------------------------------------------------
  // step 1: Clone stylesheets so portaled LiveKit UI looks correct
  // ---------------------------------------------------------------
  for (const node of Array.from(document.styleSheets)) {
    try {
      const sheet = node as CSSStyleSheet;
      if (sheet.href) {
        const link = pipDoc.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        pipDoc.head.appendChild(link);
        continue;
      }
      const rules = sheet.cssRules;
      if (!rules) continue;
      const style = pipDoc.createElement("style");
      style.textContent = Array.from(rules)
        .map((r) => r.cssText)
        .join("\n");
      pipDoc.head.appendChild(style);
    } catch {
      /* cross-origin sheets may throw */
    }
  }
  const base = pipDoc.createElement("style");
  base.textContent = `
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: #0b0f17;
      color: #e2e8f0;
      font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    }
    #live-session-pip-root {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
    }
  `;
  pipDoc.head.appendChild(base);
  pipDoc.body.classList.add(LIVE_SESSION_FLOW_CLASS);
}

export async function openDocumentPictureInPicture(options?: {
  width?: number;
  height?: number;
}): Promise<DocumentPipWindow | null> {
  if (!isDocumentPictureInPictureSupported()) return null;
  const dpi = (
    window as unknown as {
      documentPictureInPicture: {
        requestWindow: (o?: {
          width?: number;
          height?: number;
        }) => Promise<Window>;
        window?: Window | null;
      };
    }
  ).documentPictureInPicture;

  // Close any existing DPIP window first
  try {
    dpi.window?.close();
  } catch {
    /* ignore */
  }

  const pip = (await dpi.requestWindow({
    width: options?.width ?? 360,
    height: options?.height ?? 420,
  })) as DocumentPipWindow;

  copyStylesToPip(pip.document);
  const root = pip.document.createElement("div");
  root.id = "live-session-pip-root";
  pip.document.body.appendChild(root);
  return pip;
}
